import { http } from "./http";
import type { LinkedDnis } from "./agentsApi";

export type { LinkedDnis };

/**
 * Huntgroup overview row — matches HuntGroupListItemDto in the
 * .NET HuntGroupsController. The columns mirror the legacy
 * `spHuntGroup_List_V02` projection plus a fresh availability count
 * computed in the repository: `assignedOnline` / `assignedTotal`
 * destinations (Agent.statusId = 2 counts as "online").
 *
 * `agentsAvail` is the legacy 0/1 flag and is preserved for any older
 * consumers; new UI should prefer the explicit counts.
 */
export interface HuntGroupListItem {
  huntGroupId: number;
  name: string;
  distribution: string;
  limit: string;
  agentsAvail: boolean;
  assignedTotal: number;
  assignedOnline: number;
}

export interface HuntGroupListResponse {
  items: HuntGroupListItem[];
}

/**
 * Whether the huntgroup will actually ring a destination right now: the agent
 * status must be Available (2) and a PBX extension must be registered.
 */
export interface HuntGroupAgentState {
  statusId: number;
  statusName: string;
  pbxEnabled: boolean;
  pbxRegistered: boolean;
}

export interface HuntGroupAgent extends HuntGroupAgentState {
  agentId: number;
  agentName: string;
  agentRef: string;
  order: number;
  prio: number;
}

export interface HuntGroupAgentSummary extends HuntGroupAgentState {
  agentId: number;
  agentName: string;
  agentRef: string;
}

export interface HuntGroupDetail {
  huntGroupId: number;
  hgName: string;
  hgRef: string;
  description: string;
  queueDistributionTypeId: number;
  queueLimitTypeId: number;
  queueLimit: number;
  postCallHandle: number;
  recordCalls: boolean;
  assignedAgents: HuntGroupAgent[];
  availableAgents: HuntGroupAgentSummary[];
  /**
   * DNIS records currently routed (by name-prefix) to this huntgroup.
   * Used by the SPA to surface a "linked to N" warning above the
   * <code>hgName</code> input on the detail page.
   */
  linkedDnis: LinkedDnis[];
}

export interface HuntGroupSavePayload {
  hgName: string;
  hgRef: string;
  description: string;
  queueDistributionTypeId: number;
  queueLimitTypeId: number;
  queueLimit: number;
  postCallHandle: number;
  recordCalls: boolean;
}

export const huntgroupsApi = {
  list: () => http<HuntGroupListResponse>("/api/huntgroups"),
  get: (id: number) => http<HuntGroupDetail>(`/api/huntgroups/${id}`),
  create: (payload: HuntGroupSavePayload) =>
    http<HuntGroupDetail>("/api/huntgroups", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  update: (id: number, payload: HuntGroupSavePayload) =>
    http<HuntGroupDetail>(`/api/huntgroups/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  remove: (id: number) =>
    http<void>(`/api/huntgroups/${id}`, { method: "DELETE" }),
  setAgent: (
    id: number,
    payload: { agentId: number; order: number; prio: number },
  ) =>
    http<HuntGroupDetail>(`/api/huntgroups/${id}/agents`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  removeAgent: (id: number, agentId: number) =>
    http<HuntGroupDetail>(`/api/huntgroups/${id}/agents/${agentId}`, {
      method: "DELETE",
    }),
};
